<template>
  <div
    ref="places"
    class="places"
    :class="{
      'is-focused': true,
      'has-error': bookingPlacesError,
    }"
  >
    <Transition>
      <Teleport to="body">
        <div
          v-show="isMenuOpen && !isDesktop"
          id="places-suggestions"
        >
          <span>
            <input
              id="places-autocomplete-in-menu"
              ref="places-autocomplete-input-in-menu"
              v-model="value"
              class="places-autocomplete"
              :placeholder="placeholderText"
              autocomplete="off"
              enterkeyhint="go"
              @focus="focusHandler"
              @blur="blurHandler"
              @input="inputHandler"
              @change="inputHandler"
              @keydown.enter="pressEnterHandler"
            />
            <button
              class="empty-input"
              @click="emptyInput"
            >
              <span class="sr-only">{{
                translationsStore.t("search_bar_clear_button")
              }}</span>
            </button>
          </span>
          <div class="suggestions">
            <div
              v-for="group in [
                filteredOptions.find((group) => group.key === 'Places'),
              ]"
              :key="group?.key"
              class="group-places"
            >
              <template v-if="group">
                <p class="group-heading">{{ group.label }}</p>

                <div>
                  <button
                    v-for="child in group.children"
                    :key="child"
                    class="item-place item-button"
                    @click="selectHandler(child)"
                  >
                    {{ child }}
                  </button>
                </div>
              </template>
            </div>
          </div>
          <div
            v-if="!isDesktop"
            class="footer-sticky"
          >
            <AnimatedButton
              label="Retour"
              @click="
                showDropdown = false;
                isMenuOpen = false;
              "
            />
          </div>
        </div>
      </Teleport>
    </Transition>
    <Transition>
      <div
        v-show="
          (isFocusedInput || isFocusedOption) && isDesktop && filteredOptions[0]
        "
        id="places-suggestions"
      >
        <div class="suggestions desktop">
          <div
            v-for="group in [
              filteredOptions.find((group) => group.key === 'Places'),
            ]"
            :key="group?.key"
            class="group-places"
          >
            <template v-if="group">
              <p class="group-heading">{{ group.label }}</p>

              <div>
                <button
                  v-for="child in group.children"
                  :key="child"
                  class="item-place item-button"
                  @mousedown.prevent="selectHandler(child)"
                  @touchstart.prevent="selectHandler(child)"
                  @keydown.enter.prevent="selectHandler(child)"
                >
                  {{ child }}
                </button>
              </div>
            </template>
          </div>
        </div>
      </div>
    </Transition>

    <div class="input-wrapper">
      <input
        id="places-autocomplete"
        ref="places-autocomplete-input"
        v-model="value"
        class="places-autocomplete"
        :placeholder="placeholderText"
        autocomplete="off"
        @focus="focusHandler"
        @blur="blurHandler"
        @input="inputHandler"
        @keydown.enter="pressEnterHandler"
      />
      <button
        class="empty-input desktop"
        @click="emptyInput"
      >
        <span class="sr-only">{{
          translationsStore.t("search_bar_clear_button")
        }}</span>
      </button>
    </div>
    <div class="booking-places-feedback">
      {{ translationsStore.t("searchbar_error_places") }}
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, nextTick, useTemplateRef, watch } from "vue";
import { useQuery } from "villus";
import { useTranslationsStore } from "../stores/translations";
import { usePlaceStore } from "../stores/place";
import { useBookingStore } from "../stores/booking";
import AnimatedButton from "./AnimatedButton.vue";
import { useViewport } from "../composables/useViewport";
import { graphqlPlacesGetDefaultPanel } from "../utils/queryGraphql";

defineProps({
  isBookingHome: Boolean,
  bookingPlacesError: Boolean,
  placeholderText: {
    type: String,
    default: () => "",
  },
  localServiceFamily: {
    type: String,
    default: () => "",
  },
  localJourneyType: {
    type: String,
    default: () => "",
  },
});

const value = ref("");
const places = ref(null);
const isFocusedInput = ref(false);
const isFocusedOption = ref(false);
const isMenuOpen = ref(false);
const defaultPanelData = ref({});
const translationsStore = useTranslationsStore();
const placeStore = usePlaceStore();
const bookingStore = useBookingStore();
const { isDesktop } = useViewport();

const emit = defineEmits(["inputValue", "pressEnter"]);

const userInput = ref("");

const placesAutoCompleteInput = useTemplateRef("places-autocomplete-input");
const placesAutoCompleteInputInMenu = useTemplateRef(
  "places-autocomplete-input-in-menu"
);

const selectHandler = (passedValue, shouldFocus = true) => {
  if (!passedValue) {
    return false;
  }
  isFocusedInput.value = false;
  isFocusedOption.value = false;
  userInput.value = passedValue;
  value.value = passedValue;
  console.log("select value");
  emit("inputValue", passedValue);
  if (shouldFocus) {
    placesAutoCompleteInput.value.focus();
    placesAutoCompleteInputInMenu.value.focus();
  }
  filteredOptions.value = [];
};

const inputHandler = (e) => {
  isFocusedInput.value = true;
  isMenuOpen.value = true;
  userInput.value = e.target?.value;
  if (e.target.value === "") {
    placeStore.updateInput("");
  }
  emit("inputValue", e.target.value);
  //filteredOptions.value = [];
};

const blurHandler = (e) => {
  if (isDesktop.value) {
    if (value.value && value.value !== "") {
      isFocusedInput.value = false;
      isFocusedOption.value = false;
      e.target.value = value.value;
    }

    setTimeout(() => {
      if (e.target.value === "") {
        isFocusedInput.value = false;
      }
    }, 200);
  } else {
    if (e.relatedTarget?.classList.contains("item-button")) {
      userInput.value = e.relatedTarget.textContent;
    } else {
      isFocusedInput.value = false;
      e.target.value = userInput.value;
    }
  }
};

const handleKeyboardNavigation = () => {
  const options = document.querySelectorAll(
    ".suggestions.desktop .item-button"
  );
  const firstOption = options[0];
  const lastOption = options[options.length - 1];

  placesAutoCompleteInput.value?.addEventListener("keydown", (event) => {
    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      isFocusedOption.value = true;
      //focus to clear button or first option
      if (
        document.querySelector(
          ".places-autocomplete:placeholder-shown + .empty-input.desktop"
        )
      ) {
        firstOption?.focus();
      } else {
        document.querySelector(".empty-input.desktop").focus();
        isFocusedOption.value = false;
      }
    } else if (event.key === "Tab" && event.shiftKey) {
      event.preventDefault();
      isFocusedOption.value = false;
      //focus back on booking select or service picker
      if (document.querySelector(".select-service")) {
        document.querySelector(".select-service button").focus();
      } else {
        document.querySelector(".n-base-selection-label[tabindex]")?.focus();
      }
    }
  });

  //focus back on input
  firstOption?.addEventListener("keydown", (event) => {
    if (event.key === "Tab" && event.shiftKey) {
      event.preventDefault();
      placesAutoCompleteInput.value.focus();
    }
  });

  //focus to cta
  lastOption?.addEventListener("keydown", (event) => {
    if (event.key === "Tab" && !event.shiftKey) {
      event.preventDefault();
      isFocusedOption.value = false;
      document.querySelector(".search-bar .booking-button button")?.focus();
    }
  });
};

const focusHandler = (e) => {
  isMenuOpen.value = true;
  if (isDesktop.value) {
    isFocusedInput.value = value.value ? false : true;
    e.target.value = value.value !== undefined ? value.value : "";
    handleKeyboardNavigation();
  } else {
    e.target.value = userInput.value;
  }
  nextTick(() => {
    placesAutoCompleteInputInMenu.value.focus();
  });
};
const pressEnterHandler = () => {
  emit("pressEnter");
  isFocusedInput.value = false;
  isFocusedOption.value = false;
  isMenuOpen.value = false;
};

const { onData } = useQuery({
  query: graphqlPlacesGetDefaultPanel,
});

onData((panelData) => {
  const rawList = Object.groupBy(
    panelData.metaobjects?.nodes[0]?.centers?.references?.nodes,
    ({ region }) => region.value
  );

  const centersByRegion = Object.values(rawList).map((region) => {
    const label = region[0]?.region?.value;
    const type = "group";
    const key = region[0]?.region?.value;
    const children = region.map((center) => {
      return {
        label: center.address?.value,
        value: center.name?.value,
        forcedLabel: center.name?.value,
      };
    });

    return {
      type,
      label,
      key,
      children,
    };
  });

  defaultPanelData.value = {
    centersByRegion,
    places: JSON.parse(panelData.metaobjects?.nodes[0]?.places?.value),
  };
});

/*
filtering must be done on the list of cities and on the list of centers
since the list of centers is organized by region the method is more complicated
 */
const filteredOptions = computed(() => {
  const places = defaultPanelData.value?.places ?? [];
  const centersByRegion = defaultPanelData.value?.centersByRegion ?? [];

  if (!value.value) {
    return [
      {
        type: "group",
        label: translationsStore.t("booking_places_cities_suggestions"),
        key: "Places",
        children: [...places],
      },
      {
        type: "group",
        label: translationsStore.t("booking_places_centers_suggestions"),
        key: "Centers",
        children: [...centersByRegion],
      },
    ];
  }

  const filteredPlaces = places.filter((option) => {
    return option.toLowerCase().includes(value.value.toLowerCase());
  });

  const filteredCenters = centersByRegion
    .filter((option) => {
      return option.children.filter((option) => {
        return option.forcedLabel
          .toLowerCase()
          .includes(value.value.toLowerCase());
      });
    })
    .map((region) => {
      return {
        key: region.key,
        label: region.label,
        type: "group",
        children: region.children.filter((center) => {
          return center.forcedLabel
            .toLowerCase()
            .includes(value.value.toLowerCase());
        }),
      };
    })
    .filter((region) => {
      return region.children.length > 0;
    });

  return [
    filteredPlaces.length > 0 && {
      type: "group",
      label: translationsStore.t("booking_places_cities_suggestions"),
      key: "Places",
      children: [...filteredPlaces],
    },
    filteredCenters.length > 0 && {
      type: "group",
      label: translationsStore.t("booking_places_centers_suggestions"),
      key: "Centers",
      children: [...filteredCenters],
    },
  ];
});

onMounted(() => {
  if (bookingStore.allCenters.length > 0) {
    const formattedAddress = placeStore.formattedAddress;
    if (formattedAddress) {
      value.value = formattedAddress;
      selectHandler(formattedAddress, false);
    }
  } else {
    value.value = placeStore.lastInput;
    selectHandler(placeStore.lastInput, false);
  }
});

const emptyInput = () => {
  value.value = "";
  placeStore.updateInput("");
  placesAutoCompleteInput.value.value = "";
  placesAutoCompleteInputInMenu.value.value = "";
  userInput.value = "";
  emit("inputValue", "");
};

defineExpose({
  emptyInput,
});

watch(
  () => placeStore.input,
  () => {
    // console.log(
    //   "placeStore.input changed",
    //   placeStore.input
    // );
    value.value = placeStore.input;
  }
);
</script>

<style scoped>
.places {
  border: none;
  position: relative;
  text-transform: uppercase;
  margin-top: 1rem;
}

@media screen and (max-width: 999px) {
  .places {
    border-bottom: 1px solid rgba(0 0 0 / 10%);
  }
}

.booking-places-feedback {
  color: rgb(var(--brand-black) / 1);
  position: absolute;
  bottom: -30px;
  display: none;
  text-transform: none;
  white-space: nowrap;
  font-size: 0.8125rem;
}

.places.has-error .booking-places-feedback {
  display: block;
}

.places.has-error {
  margin-bottom: 40px;
  border: 1px solid rgb(var(--brand-black) / 1);
}

@media screen and (min-width: 1000px) {
  .places {
    margin: 0;
  }
  .places.has-error {
    margin-bottom: 0;
    height: 58px;
  }

  .places.glass.has-error {
    border-color: rgb(var(--brand-white) / 1);
    height: 4rem;
  }

  .places.glass.is-focused {
    height: 62px;
  }

  .places.glass.has-error.is-focused {
    height: calc(4rem - 1px);
  }

  :deep(.n-input-wrapper) {
    width: 100%;
    padding-right: 4rem;
  }

  .booking-places-feedback {
    bottom: -1.25rem;
  }

  .glass .booking-places-feedback {
    color: rgb(var(--brand-white) / 1);
    bottom: -1.25rem;
  }
}
.places-autocomplete {
  all: unset;
  line-height: 3.5rem;
  display: block;
  background: transparent;
  color: rgb(var(--brand-black) / 1);
  padding: 0 1.5rem;
  width: 100%;
  box-sizing: border-box;
}
.glass .places-autocomplete {
  color: rgb(var(--brand-white) / 1);
  line-height: 4rem;
}
.empty-input {
  all: unset;
  background-image: url(../assets/icons/close-black.svg);
  background-position: center center;
  background-repeat: no-repeat;
  display: block;
  position: absolute;
  right: 2rem;
  height: 16px;
  width: 16px;
  cursor: pointer;
  top: calc((3.5rem - 16px) / 2);
  z-index: 3;
}
.empty-input:focus-visible {
  outline: 1px solid rgb(var(--brand-primary) / 1);
}
.glass .empty-input {
  background-image: url(../assets/icons/close-white.svg);
  top: calc((4rem - 16px) / 2);
  color: rbg(var(--brand-white) / 1);
}
.places-autocomplete:placeholder-shown + .empty-input {
  display: none;
}
.glass .places-autocomplete::placeholder {
  color: rgb(var(--brand-white) / 1);
  opacity: 0.5;
}
#places-suggestions {
  background: rgb(var(--brand-white) / 1);
  position: absolute;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;
  box-sizing: border-box;
  bottom: unset;
  top: calc(100%);
  border: 1px solid rgba(0 0 0 / 10%);
  z-index: 300;
  max-height: 25vh;
  overflow-x: hidden;
  overflow-y: auto;
}
.glass #places-suggestions {
  top: unset;
  bottom: 100%;
}
.group-heading {
  color: #8a8a8a;
  font-size: 0.875rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1;
}
.sub-group-heading {
  color: rgb(var(--brand-black) / 1);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 400;
  line-height: 1;
}
.item-place {
  all: unset;
  display: block;
  color: rgb(var(--brand-black) / 1);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  letter-spacing: 0.44px;
  text-transform: uppercase;
  padding: 0.75rem 2rem 0.75rem 0rem;
  cursor: pointer;
  pointer-events: all;
  width: 100%;
}
.item-place:first-child {
  padding-block-start: 0;
}
.item-place:focus-visible {
  text-decoration: underline;
}
.item-center {
  background: #fafafa;
  padding: 1rem 1rem 1rem 3.3rem;
  display: flex;
  gap: 1rem;
  background-image: url(../assets/icons/center.svg);
  background-position: 1rem center;
  background-repeat: no-repeat;
}
.item-center button {
  all: unset;
  color: rgb(var(--brand-black) / 1);
  font-size: 0.75rem;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  text-transform: uppercase;
  cursor: pointer;
  pointer-events: all;
}
.group-places,
.group-centers,
.group-region,
.region-centers {
  display: flex;
  flex-direction: column;
}
.group-places,
.group-centers {
  gap: 1.5rem;
}
.group-region,
.region-centers {
  gap: 1rem;
}

.v-enter-active,
.v-leave-active {
  transition: all 0.1s ease;
}
.v-enter-from,
.v-leave-to {
  opacity: 0;
  transform: scale(0, 0);
  transform-origin: top left;
}

/* completion menu for mobile */
@media screen and (max-width: 999px) {
  #places-suggestions {
    background: rgb(var(--brand-white) / 1);
    position: fixed;
    padding: 1.5rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
    box-sizing: border-box;
    top: 0px !important;
    right: 0px !important;
    bottom: 0px !important;
    left: 0px !important;
    border: none;
    z-index: 600;
    max-height: 100vh;
    overflow-y: auto;
    overflow-x: hidden;
  }
  .suggestions {
    position: absolute;
    top: 7rem;
    left: 0;
    overflow-y: scroll;
    width: 100%;
    padding: 0 1.5rem;
    height: calc(100vh - 7rem - 5.5rem - 1.5rem);
    overflow-y: scroll;
  }
  .footer-sticky {
    position: fixed;
    bottom: 0;
    left: 0;
    width: 100%;
    background: rgb(var(--brand-white) / 1);
    padding: 1.5rem 1.25rem;
    z-index: 10;
    box-shadow: 0 -4px 36px 0 rgba(0 0 0 / 10%);
  }
  .input-wrapper {
    height: 4rem;
  }
  #places-suggestions .places-autocomplete {
    position: fixed;
    top: 1.5rem;
    right: 1.5rem;
    left: 1.5rem;
    width: calc(100vw - 3rem);
    z-index: 1;
    border: 1px solid rgba(0 0 0 / 10%);
    text-transform: uppercase;
  }
  .places-autocomplete {
    padding: 0;
  }
  .glass .places-autocomplete {
    color: rgb(var(--brand-black) / 1);
  }
  .glass .places-autocomplete::placeholder {
    color: rgb(var(--brand-black) / 1);
    opacity: 0.5;
  }
  #places-autocomplete-in-menu {
    padding: 0 1.5rem;
  }
  #places-autocomplete-in-menu + .empty-input {
    background-image: url(../assets/icons/close-black.svg);
    top: calc(1.5rem + (3.5rem / 2) - (16px / 2));
    position: fixed;
    z-index: 3;
    right: 3rem;
  }
  .empty-input,
  .glass .empty-input {
    background-image: url(../assets/icons/close-black.svg);
    right: 0;
    top: calc((4rem - 16px) / 2);
    position: absolute;
  }
}
</style>

<style>
.search-button {
  display: none;
  width: 3rem;
  height: 100%;
  position: absolute;
  top: 0;
  right: 0;
  cursor: pointer;
  z-index: 10;

  @media screen and (min-width: 1000px) {
    width: 4rem;
  }
}
</style>
